// UI-2: TabGroup, real tabs. design-brief.md "Tabs (pill group)"; addendum 4 on a phone.
// role="tablist" / role="tab" / role="tabpanel"; Left and Right (and Home, End) move AND
// select, as the WAI-ARIA tabs pattern with automatic activation; only the selected tab is
// in the Tab order. Each tab has its own pastel (a token name). On a phone the row never
// wraps: it scrolls sideways with snap, the scrollbar is hidden, a fade shows on the right
// while more tabs are off screen, and the selected tab is scrolled into view.
import React, { useEffect, useId, useRef, useState } from 'react';
import { v } from './tokens';
import './v2.css';

// tabs: [{ id, label, color: 'pastel-blue', count?, tourId? }]. tourId becomes the tab's data-tour-id
// (a guided tour's target), as a ChipGroup option's does.
export function TabGroup({ tabs, value, onChange, label, idPrefix }) {
  const auto = useId();
  const prefix = idPrefix || auto;
  const listRef = useRef(null);
  const [moreRight, setMoreRight] = useState(false);

  const measure = () => {
    const el = listRef.current;
    if (!el) return;
    setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  };

  // The selected tab into view (on load and on change), and the edge fade.
  useEffect(() => {
    const el = listRef.current;
    const tab = el?.querySelector('[aria-selected="true"]');
    if (el && tab && el.scrollWidth > el.clientWidth) {
      const left = tab.offsetLeft - el.offsetLeft;
      if (left < el.scrollLeft || left + tab.offsetWidth > el.scrollLeft + el.clientWidth) {
        el.scrollLeft = Math.max(0, left - 16);
      }
    }
    measure();
  }, [value]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const onKey = (e) => {
    const i = tabs.findIndex((t) => t.id === value);
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    onChange(tabs[next].id);
    // Focus after React has re-rendered the new selection.
    const id = `${prefix}-tab-${tabs[next].id}`;
    setTimeout(() => document.getElementById(id)?.focus(), 0);
  };

  return (
    <div className="ui-tabs-wrap" data-more-right={moreRight ? 'true' : 'false'}>
      <div ref={listRef} role="tablist" aria-label={label} className="ui-tabs" onKeyDown={onKey} onScroll={measure}>
        {tabs.map((t) => {
          const selected = t.id === value;
          return (
            <button key={t.id} id={`${prefix}-tab-${t.id}`} type="button" role="tab"
              aria-selected={selected} aria-controls={`${prefix}-panel-${t.id}`} tabIndex={selected ? 0 : -1}
              className="ui-tab" style={{ '--ui-bg': v(t.color || 'pastel-neutral') }} data-tour-id={t.tourId || undefined}
              onClick={() => onChange(t.id)}>
              {t.label}
              {t.count !== undefined && t.count !== null && <span className="ui-tab__count">{t.count}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// The panel for the selected tab; pass the same idPrefix as the TabGroup.
export function TabPanel({ idPrefix, tabId, children }) {
  return (
    <div role="tabpanel" id={`${idPrefix}-panel-${tabId}`} aria-labelledby={`${idPrefix}-tab-${tabId}`} tabIndex={0} className="ui-tabpanel">
      {children}
    </div>
  );
}
